import React from 'react';

import { useIntl } from '@kamernet/core/Intl';
import { Locale } from '@kamernet/core/Intl/types';
import {
  ListingsSearchCategory,
  getMainListingsSearchCategory,
} from '@kamernet/core/Routing/routes/ListingsSearch';

import { ListingsSearchAllFilters } from '@kamernet/modules/Search/types';
import {
  isCitySearch,
  isCountrySearch,
  isStreetSearch,
} from '@kamernet/modules/Search/utilities';

// Country
function getCountryMetaTitleEN(
  mainSearchCategory: ListingsSearchCategory,
): string {
  const map: Record<ListingsSearchCategory, string> = {
    [ListingsSearchCategory.Empty]: '',
    [ListingsSearchCategory.Generic]:
      'Housing in Netherlands, Houses for rent in Netherlands',
    [ListingsSearchCategory.Room]: 'Rooms for Rent in Netherlands',
    [ListingsSearchCategory.Apartment]: 'Apartments for Rent in Netherlands',
    [ListingsSearchCategory.Studio]:
      'Studio Apartments for Rent in Netherlands',
    [ListingsSearchCategory.Antisquat]: 'Anti-Squat in Netherlands',
    [ListingsSearchCategory.StudentHousing]: 'Student housing in Netherlands',
    [ListingsSearchCategory.FurnishedApartment]:
      'Furnished Rentals in Netherlands - Updated Daily',
    [ListingsSearchCategory.ShortTermRental]:
      'Short Term Rentals in Netherlands - Updated Daily',
    [ListingsSearchCategory.LongTermRental]:
      'Long Term Rentals in Netherlands - Updated Daily',
  };

  return map[mainSearchCategory];
}

function getCountryMetaTitleNL(
  mainSearchCategory: ListingsSearchCategory,
): string {
  const map: Record<ListingsSearchCategory, string> = {
    [ListingsSearchCategory.Empty]: '',
    [ListingsSearchCategory.Generic]:
      'Huurwoningen in Nederland, Huurhuizen in Nederland',
    [ListingsSearchCategory.Room]: 'Kamer te Huur in Nederland',
    [ListingsSearchCategory.Apartment]: 'Appartement te Huur in Nederland',
    [ListingsSearchCategory.Studio]: 'Studio te Huur in Nederland',
    [ListingsSearchCategory.Antisquat]: 'Antikraak wonen in Nederland',
    [ListingsSearchCategory.StudentHousing]: 'Studentenwoningen in Nederland',
    [ListingsSearchCategory.FurnishedApartment]:
      'Gemeubileerde Huren in Nederland -  Dagelijks Bijgewerkt',
    [ListingsSearchCategory.ShortTermRental]:
      'Tijdelijke Woning Huren in Nederland - Dagelijks Bijgewerkt',
    [ListingsSearchCategory.LongTermRental]:
      'Lange Termijn Huren in Nederland -  Dagelijks Bijgewerkt',
  };

  return map[mainSearchCategory];
}

function getCountryMetaDescriptionEN(
  mainSearchCategory: ListingsSearchCategory,
): string {
  const map: Record<ListingsSearchCategory, string> = {
    [ListingsSearchCategory.Empty]: '',
    [ListingsSearchCategory.Generic]:
      'Housing in Netherlands. Kamernet Offers Affordable Houses for Rent in Netherlands. Rent a House in Netherlands Today That Is ✓International-Friendly ✓Move-In-Ready.',
    [ListingsSearchCategory.Room]:
      'Rooms for rent in Netherlands. Find student room, shared room, spare room and private room to rent in Netherlands. Visit Kamernet and Rent a room in Netherlands today.',
    [ListingsSearchCategory.Apartment]:
      'Netherlands apartments for rent. Find 1-bed, 2-bed, long-term, fully furnished apartments and flats to rent in Netherlands. Visit Kamernet and rent apartment in Netherlands today.',
    [ListingsSearchCategory.Studio]:
      'Studio to rent in Netherlands. Find furnished studios and long-term studios for rent in Netherlands. Contact landlords directly and rent a studio in Netherlands today.',
    [ListingsSearchCategory.Antisquat]:
      'Anti-Squat Netherlands. Find affordable anti squat housing in Netherlands. Visit Kamernet to rent an anti squat in Netherlands today.',
    [ListingsSearchCategory.StudentHousing]:
      'Student housing in Netherlands. Find student apartments and rooms for students in Netherlands. Rent international student accommodation in Netherlands close to universities.',
    [ListingsSearchCategory.FurnishedApartment]:
      'Furnished Apartments in Netherlands. Kamernet offers Fully Furnished Apartments for Rent in Netherlands that are ✓Well-quipped ✓Move-in ready ✓Utilities included',
    [ListingsSearchCategory.ShortTermRental]:
      'Short Term Rentals in Netherlands. Kamernet offers Cheap Apartments for Rent in Netherlands for ✓Short Stay Accommodation ✓Monthly Rentals ✓Temporary Housing',
    [ListingsSearchCategory.LongTermRental]:
      'Long Term Rentals in Netherlands. Kamernet offers Cheap Houses for Rent in Netherlands for Long Term Stays. ✓Verified listings ✓Affordable rent ✓No agency fees',
  };

  return map[mainSearchCategory];
}

function getCountryMetaDescriptionNL(
  mainSearchCategory: ListingsSearchCategory,
): string {
  const map: Record<ListingsSearchCategory, string> = {
    [ListingsSearchCategory.Empty]: '',
    [ListingsSearchCategory.Generic]:
      'Bekijk alle huurwoningen in Nederland op Kamernet. Vind huizen te huur in Nederland en kies eenvoudig waar jij wilt wonen en huren in Nederland.',
    [ListingsSearchCategory.Room]:
      'Kamer te huur in Nederland. Vind studentenkamer, gedeelde kamer, logeerkamer, prive kamer huren in Nederland geplaatst door huisgenoten en verhuurders op Kamernet.',
    [ListingsSearchCategory.Apartment]:
      'Appartement huren in Nederland. Vind 1 slaapkamer, 2 slaapkamer, lange termijn, gemeubileerde appartementen te huur in Nederland met Kamernet.',
    [ListingsSearchCategory.Studio]:
      'Studio huren in Nederland. Vind studio appartement en gemeubileerde studio te huur in Nederland. Direct contact opnemen met verhuurders en huur studio in Nederland met Kamernet.',
    [ListingsSearchCategory.Antisquat]:
      'Antikraak Nederland. Vind antikraak woning om goedkoop te wonen in Nederland. Huur anti kraak in Nederland met Kamernet.',
    [ListingsSearchCategory.StudentHousing]:
      'Studentenwoningen in Nederland. Vind studentenappartementen en studentenkamer in Nederland. Huur internationale studentenhuisvesting in Nederland dichtbij universiteiten.',
    [ListingsSearchCategory.FurnishedApartment]:
      'Gemeubileerde appartementen te huur in Nederland. Kamernet biedt volledig gemeubileerde woning huren in Nederland die ✓Goed uitgerust ✓Instapklaar ✓Inclusief nutsvoorzieningen zijn',
    [ListingsSearchCategory.ShortTermRental]:
      'Tijdelijke woning huren in Nederland. Kamernet biedt goedkope tijdelijke woonruimte in Nederland met ✓Geverifieerde vermeldingen ✓Betaalbare huur ✓Geen bemiddelingskosten',
    [ListingsSearchCategory.LongTermRental]:
      'Lange termijn huren in Nederland. Kamernet biedt lange termijn appartementen te huur in Nederland met ✓Geverifieerde vermeldingen ✓Betaalbare huur ✓Geen bemiddelingskosten',
  };

  return map[mainSearchCategory];
}

// City
function getCityMetaTitleEN(
  cityName: string,
  mainSearchCategory: ListingsSearchCategory,
): string {
  const map: Record<ListingsSearchCategory, string> = {
    [ListingsSearchCategory.Empty]: '',
    [ListingsSearchCategory.Generic]: `Housing in ${cityName}, Houses for rent in ${cityName}`,
    [ListingsSearchCategory.Room]: `Rooms in ${cityName}, Rooms for rent in ${cityName}`,
    [ListingsSearchCategory.Apartment]: `Apartments in ${cityName}, Rental Flats in ${cityName}`,
    [ListingsSearchCategory.Studio]: `Studio in ${cityName}, Studio for rent in ${cityName}`,
    [ListingsSearchCategory.Antisquat]: `Anti-Squat in ${cityName}`,
    [ListingsSearchCategory.StudentHousing]: `Student housing and student rooms in ${cityName}`,
    [ListingsSearchCategory.FurnishedApartment]: `Furnished Apartments for Rent in ${cityName}, Netherlands`,
    [ListingsSearchCategory.ShortTermRental]: `Short Term Rentals in ${cityName}, Netherlands`,
    [ListingsSearchCategory.LongTermRental]: `Long Term Rentals in ${cityName}, Netherlands`,
  };

  return map[mainSearchCategory];
}

function getCityMetaTitleNL(
  cityName: string,
  mainSearchCategory: ListingsSearchCategory,
): string {
  const map: Record<ListingsSearchCategory, string> = {
    [ListingsSearchCategory.Empty]: '',
    [ListingsSearchCategory.Generic]: `Huurwoningen in ${cityName}, Huurhuizen in ${cityName}`,
    [ListingsSearchCategory.Room]: `Kamer ${cityName}, Kamer huren in ${cityName}`,
    [ListingsSearchCategory.Apartment]: `Appartementen ${cityName}, Huur appartement in ${cityName}`,
    [ListingsSearchCategory.Studio]: `Studio ${cityName}, Studio huren in ${cityName}`,
    [ListingsSearchCategory.Antisquat]: `Antikraak wonen in ${cityName}`,
    [ListingsSearchCategory.StudentHousing]: `Studentenwoningen en studentenkamers in ${cityName}`,
    [ListingsSearchCategory.FurnishedApartment]: `Gemeubileerde Huren in ${cityName}, Nederland`,
    [ListingsSearchCategory.ShortTermRental]: `Tijdelijk Woning Huren in ${cityName}, Nederland`,
    [ListingsSearchCategory.LongTermRental]: `Lange Termijn Huren in ${cityName}, Nederland`,
  };

  return map[mainSearchCategory];
}

function getCityMetaDescriptionEN(
  cityName: string,
  mainSearchCategory: ListingsSearchCategory,
): string {
  const map: Record<ListingsSearchCategory, string> = {
    [ListingsSearchCategory.Empty]: '',
    [ListingsSearchCategory.Generic]: `Housing in ${cityName}. Looking to Rent a House in ${cityName}? Find Your Next House for Rent in ${cityName} With Kamernet and Get in Touch With the Property Manager.`,
    [ListingsSearchCategory.Room]: `Rooms in ${cityName}. Looking to rent a room in ${cityName}? View all rooms for rent in ${cityName} on Kamernet. ✓Verified Listings ✓Affordable Rent ✓Short & Long term`,
    [ListingsSearchCategory.Apartment]: `Apartments in ${cityName}. Looking to rent an apartment in ${cityName}? View all apartments for rent in ${cityName} on Kamernet. ✓Verified Flats ✓Affordable Rent ✓Short & Long term`,
    [ListingsSearchCategory.Studio]: `Studio for rent in ${cityName}. Looking to rent a studio in ${cityName}?  View all studio apartments in ${cityName} on Kamernet. ✓Verified Listings ✓Affordable Rent ✓Short & Long term`,
    [ListingsSearchCategory.Antisquat]: `Anti-Squat ${cityName}. Find affordable anti squat housing in ${cityName}. Visit Kamernet to rent an anti squat in ${cityName} today.`,
    [ListingsSearchCategory.StudentHousing]: `Student accommodation in ${cityName}. Looking for international student housing in ${cityName}? View all student apartments and student rooms in ${cityName} on Kamernet.`,
    [ListingsSearchCategory.FurnishedApartment]: `Furnished Apartments in ${cityName} for Long-term & Short term. Find Fully Furnished Apartments for Rent in ${cityName} including Rooms & Studios that are Move-in Ready.`,
    [ListingsSearchCategory.ShortTermRental]: `Short Term Rentals in ${cityName}, Netherlands. Rent Apartment in ${cityName} for Short Term including Rooms & Studios for Temporary Accommodation.`,
    [ListingsSearchCategory.LongTermRental]: `Long Term Rentals in ${cityName}, Netherlands. Find Cheap Apartments for Rent in ${cityName} for Long Term including Rooms & Studios for Longer Stays.`,
  };

  return map[mainSearchCategory];
}

function getCityMetaDescriptionNL(
  cityName: string,
  mainSearchCategory: ListingsSearchCategory,
): string {
  const map: Record<ListingsSearchCategory, string> = {
    [ListingsSearchCategory.Empty]: '',
    [ListingsSearchCategory.Generic]: `Huurwoningen in ${cityName}. Wil je een huis huren in ${cityName}? Vind jouw volgende woning te huur in ${cityName} op Kamernet en neem contact op met de verhuurder.`,
    [ListingsSearchCategory.Room]: `Kamer zoeken in ${cityName}? Bekijk alle kamers te huur in ${cityName} op Kamernet. Vind een goedkope particulier kamer huren in ${cityName}. ✓Korte en lange termijn`,
    [ListingsSearchCategory.Apartment]: `Appartementen in ${cityName}. Zoek je appartementen te huur in ${cityName}? Bekijk alle appartement huren in ${cityName} op Kamernet. ✓Betaalbare Huur ✓Korte en lange termijn`,
    [ListingsSearchCategory.Studio]: `Studio huren in ${cityName}. Zoek je een studio te huur in ${cityName}? Bekijk alle lange en korte termijn studios in ${cityName} op Kamernet. ✓Betaalbare Huur ✓Korte en lange termijn`,
    [ListingsSearchCategory.Antisquat]: `Antikraak ${cityName}. Vind antikraak woning om goedkoop te wonen in ${cityName}. Huur anti kraak in ${cityName} met Kamernet.`,
    [ListingsSearchCategory.StudentHousing]: `Studentenwoningen in ${cityName}. Op zoek naar internationale studentenhuisvesting in ${cityName}? Bekijk alle studenten appartementen en studentenkamers in ${cityName} op Kamernet.`,
    [ListingsSearchCategory.FurnishedApartment]: `Gemeubileerde appartementen te huur in ${cityName} voor korte en lange termijn. Vind volledig gemeubileerde woning huren in ${cityName}, inclusief kamers en studio's die instapklaar zijn.`,
    [ListingsSearchCategory.ShortTermRental]: `Tijdelijke huren in ${cityName}, Nederland. Vind goedkope tijdelijke woonruimte in ${cityName}, inclusief tijdelijke appartementen en kamers voor korte termijn.`,
    [ListingsSearchCategory.LongTermRental]: `Lange termijn huren in ${cityName}, Nederland. Vind lange termijn appartementen te huur in ${cityName} inclusief kamers en studio's. ✓Geverifieerde vermeldingen ✓Betaalbare huur ✓Langere verblijven`,
  };

  return map[mainSearchCategory];
}

// Street
function getStreetMetaTitleEN(
  cityName: string,
  streetName: string,
  mainSearchCategory: ListingsSearchCategory,
): string {
  const map: Record<ListingsSearchCategory, string> = {
    [ListingsSearchCategory.Empty]: '',
    [ListingsSearchCategory.Generic]: `Houses for rent in ${streetName}, ${cityName}`,
    [ListingsSearchCategory.Room]: `Rooms for Rent in ${streetName}, ${cityName}`,
    [ListingsSearchCategory.Apartment]: `Apartments for Rent in ${streetName}, ${cityName}`,
    [ListingsSearchCategory.Studio]: `Studio Apartments for Rent in ${streetName}, ${cityName}`,
    [ListingsSearchCategory.Antisquat]: `Anti-Squat in ${streetName}, ${cityName}`,
    [ListingsSearchCategory.StudentHousing]: `Student housing in ${streetName}, ${cityName}`,
    [ListingsSearchCategory.FurnishedApartment]: `Furnished Apartments for Rent in ${streetName}, ${cityName}`,
    [ListingsSearchCategory.ShortTermRental]: `Short Term Rentals in ${streetName}, ${cityName}`,
    [ListingsSearchCategory.LongTermRental]: `Long Term Rentals in ${streetName}, ${cityName}`,
  };

  return map[mainSearchCategory];
}

function getStreetMetaTitleNL(
  cityName: string,
  streetName: string,
  mainSearchCategory: ListingsSearchCategory,
): string {
  const map: Record<ListingsSearchCategory, string> = {
    [ListingsSearchCategory.Empty]: '',
    [ListingsSearchCategory.Generic]: `Huizen te huur in ${streetName}, ${cityName}`,
    [ListingsSearchCategory.Room]: `Kamer te Huur in ${streetName}, ${cityName}`,
    [ListingsSearchCategory.Apartment]: `Appartement te Huur in ${streetName}, ${cityName}`,
    [ListingsSearchCategory.Studio]: `Studio te Huur in ${streetName}, ${cityName}`,
    [ListingsSearchCategory.Antisquat]: `Antikraak wonen in ${streetName}, ${cityName}`,
    [ListingsSearchCategory.StudentHousing]: `Studentenwoningen in ${streetName}, ${cityName}`,
    [ListingsSearchCategory.FurnishedApartment]: `Gemeubileerde Huren in ${streetName}, ${cityName}`,
    [ListingsSearchCategory.ShortTermRental]: `Tijdelijk Woning Huren in ${streetName}, ${cityName}`,
    [ListingsSearchCategory.LongTermRental]: `Lange Termijn Huren in ${streetName}, ${cityName}`,
  };

  return map[mainSearchCategory];
}

function getStreetMetaDescriptionEN(
  cityName: string,
  streetName: string,
  mainSearchCategory: ListingsSearchCategory,
): string {
  const map: Record<ListingsSearchCategory, string> = {
    [ListingsSearchCategory.Empty]: '',
    [ListingsSearchCategory.Generic]: `Looking for a House to Rent in ${streetName}, ${cityName}? With Kamernet, Find Your New House for Rent in ${streetName} Using Our Detailed Search Filters.`,
    [ListingsSearchCategory.Room]: `Rooms for rent in ${streetName}, ${cityName}. Find student room, shared room, spare room and private room to rent in ${streetName}, ${cityName}. Visit Kamernet and Rent a room in ${streetName}, ${cityName} today.`,
    [ListingsSearchCategory.Apartment]: `${streetName}, ${cityName} apartments for rent. Find 1-bed, 2-bed, long-term, fully furnished apartments and flats to rent in ${streetName}, ${cityName}. Visit Kamernet and rent apartment in ${streetName}, ${cityName} today.`,
    [ListingsSearchCategory.Studio]: `Studio to rent in ${streetName}, ${cityName}. Find furnished studios and long-term studios for rent in ${streetName}, ${cityName}. Contact landlords directly and rent a studio in ${streetName}, ${cityName} today.`,
    [ListingsSearchCategory.Antisquat]: `Anti-Squat ${streetName}, ${cityName}. Find affordable anti squat housing in ${streetName}, ${cityName}. Visit Kamernet to rent an anti squat in ${streetName}, ${cityName} today.`,
    [ListingsSearchCategory.StudentHousing]: `Student housing in ${streetName}, ${cityName}. Find student apartments and rooms for students in ${streetName}. Rent student accommodation in ${streetName}.`,
    [ListingsSearchCategory.FurnishedApartment]: `Furnished Apartments in ${streetName}, ${cityName}. Find Your Next Furnished Rental in ${streetName} Using Our Detailed Filters and Get in Touch With the Property Managers.`,
    [ListingsSearchCategory.ShortTermRental]: `Short Term Rentals in ${streetName}, ${cityName}. Find Your Next Short Stay Rental in ${streetName} Using Our Detailed Filters and Get in Touch With the Property Managers.`,
    [ListingsSearchCategory.LongTermRental]: `Long Term Rentals in ${streetName}, ${cityName}. Find Your Next Long Term Stay in ${streetName} Using Our Detailed Filters and Get in Touch With the Property Managers.`,
  };

  return map[mainSearchCategory];
}

function getStreetMetaDescriptionNL(
  cityName: string,
  streetName: string,
  mainSearchCategory: ListingsSearchCategory,
): string {
  const map: Record<ListingsSearchCategory, string> = {
    [ListingsSearchCategory.Empty]: '',
    [ListingsSearchCategory.Generic]: `Op zoek naar een woning te huur in ${streetName}, ${cityName}? Begin met huren in ${streetName} via Kamernet en vind je volgende huis eenvoudig en snel.`,
    [ListingsSearchCategory.Room]: `Kamer te huur in ${streetName}, ${cityName}. Vind studentenkamer, gedeelde kamer, logeerkamer, prive kamer huren in ${streetName}, ${cityName} geplaatst door huisgenoten en verhuurders op Kamernet.`,
    [ListingsSearchCategory.Apartment]: `Appartement huren in ${streetName}, ${cityName}. Vind 1 slaapkamer, 2 slaapkamer, lange termijn, gemeubileerde appartementen te huur in ${streetName}, ${cityName} met Kamernet.`,
    [ListingsSearchCategory.Studio]: `Studio huren in ${streetName}, ${cityName}. Vind studio appartement en gemeubileerde studio te huur in ${streetName}, ${cityName}. Direct contact opnemen met verhuurders en huur studio in ${streetName}, ${cityName} met Kamernet.`,
    [ListingsSearchCategory.Antisquat]: `Antikraak ${streetName}, ${cityName}. Vind antikraak woning om goedkoop te wonen in ${streetName}, ${cityName}. Huur anti kraak in ${streetName}, ${cityName} met Kamernet.`,
    [ListingsSearchCategory.StudentHousing]: `Studentenwoningen in ${streetName}, ${cityName}. Vind studentenappartementen en studentenkamer in ${streetName}. Huur studentenhuisvesting in ${streetName}.`,
    [ListingsSearchCategory.FurnishedApartment]: `Gemeubileerde appartementen in ${streetName}, ${cityName}. Vind uw volgende gemeubileerde huren in ${streetName} met behulp van onze gedetailleerde filters en neem contact op met de vastgoedbeheerders.`,
    [ListingsSearchCategory.ShortTermRental]: `Tijdelijke huren in ${streetName}, ${cityName}. Vind tijdelijke woonruimte op ${streetName} met behulp van onze gedetailleerde filters en neem rechtstreeks contact op met verhuurders.`,
    [ListingsSearchCategory.LongTermRental]: `Lange termijn huren in ${streetName}, ${cityName}. Vind een lange termijn woning op ${streetName} met behulp van onze gedetailleerde filters en neem rechtstreeks contact op met verhuurders.`,
  };

  return map[mainSearchCategory];
}

export const useMetaTags = (
  allFilters: ListingsSearchAllFilters,
): {
  metaTitle: string;
  metaDescription: string;
} => {
  const { locale } = useIntl();

  return React.useMemo(() => {
    let metaTitle = '';
    let metaDescription = '';

    const mainSearchCategory = getMainListingsSearchCategory(
      allFilters.searchCategories,
    );

    if (isCountrySearch(allFilters)) {
      metaTitle =
        locale === Locale.en
          ? getCountryMetaTitleEN(mainSearchCategory)
          : getCountryMetaTitleNL(mainSearchCategory);

      metaDescription =
        locale === Locale.en
          ? getCountryMetaDescriptionEN(mainSearchCategory)
          : getCountryMetaDescriptionNL(mainSearchCategory);
    }

    if (isCitySearch(allFilters)) {
      metaTitle =
        locale === Locale.en
          ? getCityMetaTitleEN(allFilters.cityName, mainSearchCategory)
          : getCityMetaTitleNL(allFilters.cityName, mainSearchCategory);

      metaDescription =
        locale === Locale.en
          ? getCityMetaDescriptionEN(allFilters.cityName, mainSearchCategory)
          : getCityMetaDescriptionNL(allFilters.cityName, mainSearchCategory);
    }

    if (isStreetSearch(allFilters)) {
      metaTitle =
        locale === Locale.en
          ? getStreetMetaTitleEN(
              allFilters.cityName,
              allFilters.streetName,
              mainSearchCategory,
            )
          : getStreetMetaTitleNL(
              allFilters.cityName,
              allFilters.streetName,
              mainSearchCategory,
            );

      metaDescription =
        locale === Locale.en
          ? getStreetMetaDescriptionEN(
              allFilters.cityName,
              allFilters.streetName,
              mainSearchCategory,
            )
          : getStreetMetaDescriptionNL(
              allFilters.cityName,
              allFilters.streetName,
              mainSearchCategory,
            );
    }

    return { metaTitle, metaDescription };
  }, [allFilters, locale]);
};
